import React, { Component, PropTypes } from 'react';
import { Link } from 'react-router';
import Bundles from 'project/containers/wrapup/bundles';
import { browserHistory } from 'react-router';
import { isCollaborator } from 'project/ducks/wrapup';
import MessageTimeline from 'common/containers/message_timeline';
import Money from 'common/components/money';
import IncreaseAwardButton from 'project/containers/wrapup/increase_award_button';
import SignContract from 'project/containers/wrapup/sign_contract';

const APPROVE_DESCR = "So you've come to the end. Before you approve the final files and end this wrap-up, make sure they reflect exactly what you want. When you click the \"Approve final files\" button below, your wrap-up will be finished and we'll issue the award to the creative."
const APPROVE_CONFIRMATION = "I have reviewed the files and have everything I need."


/**
 * 'Final Files' wrapup step.
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

const FORMATS_GUIDE_URL = '/help/working-in-projects/what-are-the-file-formats-required-by-clients/';

export default class FinalFiles extends Component {
  constructor(props) {
    super(props);

    this.handleApprove = this.handleApprove.bind(this);
  }

  componentDidMount() {
    const { wrapup, fetchMessagesIfNeeded } = this.props;
    fetchMessagesIfNeeded({
      streamId: wrapup.final_message_stream,
      wrapupStep: 'approve_files',
      wrapup
    });
  }

  handleApprove() {
    const { wrapup, user, projectUrl } = this.props;
    const isCollab = isCollaborator(wrapup, user);

    /* For collaborators, there's no survey step. Therefore, redirect user to project brief,
       since the current wrapup no longer exists for him. */
    if (isCollab) {
      browserHistory.push(projectUrl);
    }
  }

  renderHeader() {
    const { isCreative, isAdmin, wrapup } = this.props;
    const hasProofsStep = wrapup.steps.includes('approve_proofs');

    let title, description;

    if (isAdmin) {
      title = <h3>
        Final files for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

    /* TODO: REMOVE THIS IF BLOCK ONCE WE NO LONGER HAVE LEGACY WRAP-UPS */
    } else if (!isCreative && !wrapup.contract.client_signature_created_at) {
      title = <h3>
        Contract and final files for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

      description = <p>
        The contract protects both you and the creative. It ensures you own the creative's work
        and are legally protected.
      </p>;
    } else if (isCreative) {
      title = <h3>
        Final files for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
      </h3>;

      /* contract not yet signed */
      if (!wrapup.contract.creative_signature_created_at) {
        title = <h3>
          Contract and final files for Award { wrapup.award.position }
          <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
        </h3>;

        description = <p>
          The contract protects both you and the client. It ensures you are
          compensated for your work and are legally protected. We'll email
          you a signed copy of the contract after you sign it.
        </p>;
      /* contract signed, but no final files uploaded */
      } else if (wrapup.final_bundles.length === 0) {
        description = <p>
          { hasProofsStep?
          <span>The client approved your proofs.</span> :
          <span>
            We emailed you a <Link target="_blank" to={wrapup.contract.pdf_signed}>signed copy
            of the project contract</Link>.
          </span> }
          {' '}Now it's time to create your final files.
        </p>;
      /* final files uploaded, but not yet approved */
      } else if (!wrapup.final_approved_at) {
        description = <p>
          Now that you've sent final files, please be patient. It may take the client a few days
          to review the final files and approve them. They'll let you know if they require any
          changes.
        </p>;
      /* final files approved */
      } else {
        description = <p>
          Your final files have been approved.
        </p>;
      }
    } else {
      title = <h3>
        Review and approve final files for Award { wrapup.award.position }
        <span className="award-value"> (<Money value={wrapup.award.value} />)</span>
        <IncreaseAwardButton />
      </h3>;

      /* no final files uploaded */
      if (wrapup.final_bundles.length === 0) {
        description = <p>
          { !hasProofsStep && <span>We'll email you a signed copy of the contract
          when { wrapup.creative.username } signs it.<br /></span> }
          Please carefully review the final files after they're uploaded and make
          sure you have everything you need. If you need any <Link target="_blank"
          to={FORMATS_GUIDE_URL}>specific file formats</Link>, this is the time to
          let { wrapup.creative.username} know.
        </p>;
      /* final files uploaded, but not yet approved */
      } else if (!wrapup.final_approved_at) {
        description = <p>
          Please carefully review the final files after they're uploaded and make sure
          you have everything you need. Once you're happy, click the green "Approve final files
          and move to next step" button.
        </p>;
      /* final files approved */
      } else {
        description = <p>
          Final files have been approved.
        </p>;
      }
    }

    return (
      <div className="row">
        <div className="small-16 large-14 columns">
          <header className="step-header">
            { title }
            { description }
          </header>
        </div>
      </div>
    );
  }

  renderContent() {
    const {
      isCreative, wrapup, details, itemsToUpload, approveChecklist, acceptedFormats,
      isAdmin, prepopulatedMsgOpts
    } = this.props;


    /* TODO: REMOVE THIS IF BLOCK ONCE WE NO LONGER HAVE LEGACY WRAP-UPS */
    if (!isCreative && !wrapup.contract.client_signature_created_at) {
      return (
        <div className="row">
          <div className="columns small-16">
            <h5 className="text-danger">
              You have not signed the contract. You cannot<br className="hide-for-small-only"/>
              {' '}approve final files until you sign the contract.
            </h5>
            <SignContract wrapup={wrapup} signingAs="client" />
          </div>
        </div>
      );
    }

    /* contract not yet signed (creative view) */
    if (isCreative && !wrapup.contract.creative_signature_created_at) {
      return (
        <div className="row">
          <div className="columns small-16">
            <h5 className="text-danger">
              You have not signed the contract. You cannot<br className="hide-for-small-only"/>
              {' '}upload final files until you sign the contract.
            </h5>
            <SignContract wrapup={wrapup} />
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="row">
          <div className="columns medium-4 small-16">
            <h3>Files</h3>
          </div>
          <div className="columns medium-12 small-16">
            <Bundles wrapup={wrapup} bundles={wrapup.final_bundles}
              name="final files" approveDescr={APPROVE_DESCR} approveChecklist={approveChecklist}
              approvedAt={wrapup.final_approved_at} itemsToUpload={itemsToUpload}
              collaboratorApprovePerm="approve_files" acceptedFormats={acceptedFormats}
              approveConfirmation={APPROVE_CONFIRMATION} onApprove={this.handleApprove} />
          </div>
        </div>
        <div className="row">
          <div className="columns medium-4 small-16">
            <h3>Messages</h3>
          </div>
          <div className="columns medium-12 small-16">
            <MessageTimeline wrapup={wrapup} canPost={details.available} canAttach={!isCreative}
              canReply={false} editorActionLabel="Send this message" wrapupStep="approve_files"
              editorPrepopulatedOpts={prepopulatedMsgOpts} canPostInternal={!isCreative} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="wrapup-step final-files">
        { this.renderHeader() }

        { this.renderContent() }
      </div>
    );
  }
}

FinalFiles.propTypes = {
  wrapup: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  projectUrl: PropTypes.string.isRequired,
  fetchMessagesIfNeeded: PropTypes.func.isRequired,
  acceptedFormats: PropTypes.string,
  approveChecklist: PropTypes.string,
  isCreative: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  itemsToUpload: PropTypes.string,
  prepopulatedMsgOpts: PropTypes.array.isRequired
};



// WEBPACK FOOTER //
// ./project/components/wrapup/final_files.jsx